import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, NavigationError, Router, RouterModule, RouterOutlet } from '@angular/router';
import { AuthService } from './services/auth.service';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from './components/shared/loader/loader.component';
import { SetAsDynamicContainerDirective } from './components/popups/set-as-dynamic-container.directive';
import { DbService } from './services/db-service';
import { OrganizationInfo } from '../models/organization-info.interface';
import { AccountInfo } from '@azure/msal-browser';
import { MsalService } from '@azure/msal-angular';
import { InitialsPipe } from './pipes/name-initials.pipe';
import { finalize, map, Subscription } from 'rxjs';
import { ToastComponent } from './components/toast/toast.component';
import { ToastService } from './services/toast.service';
import { ToastType } from '../enums/toast-type.enum';
import { ClientModel } from '../models/ client.model';
import { UserRole } from '../enums/user-role.enum';
import { DynamicContentService } from './components/popups/dynamic-content.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OnboardingPopupComponent } from './components/onboarding-popup/onboarding-popup.component';
import { MatButtonModule } from '@angular/material/button';
import { NotificationsHistoryComponent } from './components/notifications-history/notifications-history.component';
import { CapitalizeWordsPipe } from './pipes/capitalize-wrods.pipe';
import { environment } from '../environments/environment';
import { SignalRService } from './services/signalr.service';
import { SearchSelectAutoCompleteComponent } from './components/shared/search-select-autocomplete/search-select-autocomplete.component';
import { IconSpriteComponent } from './components/shared/icon-sprite/icon-sprite.component';
import { DrawerStateService } from './services/drawer-state.service';
import { ThemeService } from './services/theme.service';
import { DEFAULT_PAGE_SIZE } from './constants/pagination.const';
@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        RouterModule,
        LoaderComponent,
        ToastComponent,
        SetAsDynamicContainerDirective,
        InitialsPipe,
        MatMenuModule,
        MatButtonModule,
        MatTooltipModule,
        NotificationsHistoryComponent,
        SearchSelectAutoCompleteComponent,
        IconSpriteComponent,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [CapitalizeWordsPipe],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
    public loading = false;
    public organizationData!: OrganizationInfo | null;
    public userName = '';
    public isAdmin = false;
    public isSuperAdmin = false;
    public clients: ClientModel[] = [];
    public clientsPageNumber = 2;
    public clientsPageSize = DEFAULT_PAGE_SIZE;
    public allClientsLoaded = false;
    public searchTerm = '';
    // Starts collapsed below the small-laptop breakpoint if the user has never
    // toggled it manually - once they do, that choice is remembered in
    // localStorage and wins over the width-based default on future loads.
    public railCollapsed = this.readRailCollapsedState();
    public drawerOpen = false;
    public libraryNavExpanded = this.readSidebarGroupState('sidebarLibraryExpanded');
    public correctionsNavExpanded = this.readSidebarGroupState('sidebarCorrectionsExpanded');
    public dashboardNavExpanded = this.readSidebarGroupState('sidebarDashboardExpanded');
    public isDarkTheme = false;

    private readonly subscriptions: Subscription[] = [];

    constructor(
        public readonly authService: AuthService,
        public readonly router: Router,
        public readonly dbService: DbService,
        private readonly msalService: MsalService,
        private readonly toastService: ToastService,
        private readonly dynamicContentService: DynamicContentService,
        private readonly capitalizeWordsPipe: CapitalizeWordsPipe,
        private readonly signalRService: SignalRService,
        private readonly drawerStateService: DrawerStateService,
        public readonly themeService: ThemeService
    ) {
        this.addGoogleScripts();
        this.handleChunkLoadErrors();
    }

    private handleChunkLoadErrors() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationError) {
                const error = event.error;

                if (error?.message?.includes('Failed to fetch dynamically imported module')) {
                    globalThis.location.reload();
                }
            }
        });
    }

    async ngOnInit(): Promise<void> {
        globalThis.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                globalThis.location.reload();
            }
        });

        this.authService.startTokenRefresh();
        this.subscriptions.push(
            this.dbService.organizationData$.subscribe((res) => {
                if (res) {
                    this.organizationData = res;
                }
            }),
            this.authService.userDetails$.subscribe((res) => {
                if (res && !res.onboardingComplete) {
                    this.dynamicContentService.openContent(OnboardingPopupComponent).afterClosed.subscribe(() => {
                        this.dbService.completeOnboarding(res.id).subscribe();
                    });
                }
            }),
            this.drawerStateService.isOpen$.subscribe((open) => {
                this.drawerOpen = open;
            }),
            this.themeService.isDark$.subscribe((isDark) => {
                this.isDarkTheme = isDark;
            })
        );

        const url = new URL(globalThis.location.href);
        const authorizationCode = url.searchParams.get('code') ?? '';
        if (authorizationCode && globalThis.opener) {
            this.dbService.salesforceAuthorize(authorizationCode).subscribe({
                next: () => {
                    globalThis.opener.postMessage({ authorizationCode }, globalThis.location.origin);
                },
                error: () => {
                    globalThis.opener.postMessage({ authorizationCode }, globalThis.location.origin);
                },
            });
            return;
        }

        this.loading = true;
        const initializedAccountAction = await this.authService.initializeAccount();
        if (initializedAccountAction === 'logout') {
            this.msalService.instance
                .handleRedirectPromise()
                .then(() => {
                    this.authService.logOut();
                })
                .catch(() => {
                    this.authService.logOut();
                })
                // Nothing else here waits for logOut()'s own redirect to land -
                // if that redirect fails (e.g. it collides with another
                // interactive call still in flight from just before this),
                // loading was otherwise never reset and the app stayed on the
                // full-screen loader forever.
                .finally(() => {
                    this.loading = false;
                });
        } else if (this.authService.isUserLoggedIn) {
            this.loading = false;
            const activeAccount: AccountInfo | null = this.msalService.instance.getActiveAccount();
            if (activeAccount && activeAccount?.idTokenClaims && activeAccount?.idTokenClaims['sub']) {
                this.authService.userDetails$.subscribe({
                    next: (res) => {
                        if (res) {
                            this.getClients();

                            const name = res?.fullName;
                            if (name) {
                                this.userName = this.capitalizeWordsPipe.transform(name);
                            }

                            const isAdmin = !!res.isAdmin;
                            const isSuperAdmin = !!res?.isSuperAdmin || !!res?.userRole?.includes(UserRole.SuperAdmin);
                            this.isAdmin = isAdmin;
                            this.isSuperAdmin = isSuperAdmin;
                            if (isAdmin || isSuperAdmin) {
                                this.getOrganizationDetails();
                            }
                        }
                    },
                });
            } else {
                this.loading = false;
                this.authService.logOut();
                this.toastService.showToast(
                    'Your session has expired or you are not authorized to access this page. Please sign in again.',
                    ToastType.Warning
                );
            }
        } else {
            this.loading = false;
        }
    }

    ngAfterViewInit(): void {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.scrollToTop();
            }
        });
    }

    loadMoreClients(keyword?: string): void {
        if (this.loading) return;
        this.loading = true;

        this.dbService
            .getClients(true, this.clientsPageNumber, this.clientsPageSize, keyword)
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (res) => {
                    this.clients = res.items;
                    this.dbService.clients.next(this.dbService.mergeClients(res.items));
                    this.clientsPageNumber++;
                    if (res.items.length < this.clientsPageSize) {
                        this.allClientsLoaded = true;
                    }
                },
            });
    }

    @ViewChild('scrollContainer', { static: false }) scrollRef!: ElementRef;
    private scrollToTop() {
        if (this.scrollRef) {
            this.scrollRef.nativeElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s?.unsubscribe());
        this.authService.stopTokenRefresh();
        this.signalRService.stopConnection();
    }

    public async logOut(): Promise<void> {
        this.loading = true;
        await this.authService.logOut();
    }

    public async logIn(): Promise<void> {
        this.loading = true;
        await this.authService.logIn();
    }

    public toggleRail(): void {
        this.railCollapsed = !this.railCollapsed;
        localStorage.setItem('sidebarRailCollapsed', String(this.railCollapsed));
    }

    public toggleLibraryNav(): void {
        if (this.railCollapsed) {
            this.router.navigate(['/library']);
            return;
        }
        this.libraryNavExpanded = !this.libraryNavExpanded;
        localStorage.setItem('sidebarLibraryExpanded', String(this.libraryNavExpanded));
    }

    public toggleCorrectionsNav(): void {
        if (this.railCollapsed) {
            this.router.navigate(['/upload']);
            return;
        }
        this.correctionsNavExpanded = !this.correctionsNavExpanded;
        localStorage.setItem('sidebarCorrectionsExpanded', String(this.correctionsNavExpanded));
    }

    public get isLibraryGroupActive(): boolean {
        return this.router.url.startsWith('/library');
    }

    public get isCorrectionsGroupActive(): boolean {
        return ['/upload', '/scanned', '/reviewed'].some((path) => this.router.url.startsWith(path));
    }

    public toggleDashboardNav(): void {
        if (this.railCollapsed) {
            this.router.navigate(['/dashboard/general']);
            return;
        }
        this.dashboardNavExpanded = !this.dashboardNavExpanded;
        localStorage.setItem('sidebarDashboardExpanded', String(this.dashboardNavExpanded));
    }

    public get isDashboardGroupActive(): boolean {
        return this.router.url.startsWith('/dashboard');
    }

    private readSidebarGroupState(key: string): boolean {
        const stored = localStorage.getItem(key);
        return stored === null ? true : stored === 'true';
    }

    private readRailCollapsedState(): boolean {
        const stored = localStorage.getItem('sidebarRailCollapsed');
        return stored === null ? window.innerWidth <= 1240 : stored === 'true';
    }

    private getOrganizationDetails(): void {
        this.dbService.getOrganizationDetails().subscribe({
            next: (res) => {
                if (res) {
                    this.dbService.organizationData$.next(res);
                    this.organizationData = res;
                }
            },
            error: () => {
                this.loading = false;
            },
            complete: () => {
                this.loading = false;
            },
        });
    }

    private getClients(): void {
        this.dbService.getClients().subscribe({
            next: (res) => {
                this.clients = res.items;
                this.dbService.clients.next(res.items);
                if (res.items.length < this.clientsPageSize) {
                    this.allClientsLoaded = true;
                }

                if (!this.dbService.selectedClientId || res.items.length === 1) {
                    this.dbService.selectedClientId = res.items[0]?.clientId?.toString();
                    this.dbService.selectedClient = res.items[0];
                    localStorage.setItem('selectedClientId', this.dbService.selectedClientId.toString());
                }

                this.signalRService.startConnection();
            },
        });
    }

    public get showShell(): boolean {
        return this.router.url !== '/login' && this.router.url !== '/forbidden';
    }

    // The unsubscribe link goes out to external recipients who were never
    // logged in - but if it's opened in a browser where an admin happens to
    // already have a session (e.g. testing their own link), the shell would
    // otherwise leak that admin's sidebar/avatar/notifications onto what's
    // meant to look like a bare public page.
    public get isUnsubscribeRoute(): boolean {
        return this.router.url.startsWith('/unsubscribe');
    }

    public selectClient(client: ClientModel): void {
        if (client.clientId?.toString() !== this.dbService.selectedClientId) {
            this.dbService.selectedClientId = client.clientId.toString();
            this.dbService.selectedClient = client;
            localStorage.setItem('selectedClientId', client.clientId.toString());
            this.dbService.selectedClientChange$.next(client);
            this.refreshComponent();
            this.signalRService.stopConnection(true);
        }
    }

    public refreshComponent(): void {
        const currentUrl = this.router.url;
        this.router.navigateByUrl(currentUrl);
    }

    public loadClientFromStorage(): void {
        this.dbService.selectedClientId = localStorage.getItem('selectedClientId');
    }

    public getSelectedClient(): ClientModel | undefined {
        return this.dbService.clients.value.find((c) => c.clientId.toString() === this.dbService.selectedClientId);
    }

    private addGoogleScripts() {
        if (environment.production) {
            const script = document.createElement('script');
            script.src = `https://www.googletagmanager.com/gtag/js?id=G-57WYS43SKV`;
            script.async = true;
            document.head.appendChild(script);

            const googleScript = document.createElement('script');
            googleScript.innerHTML = `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-57WYS43SKV');
            `;
            document.head.appendChild(googleScript);

            const recaptchaSript = document.createElement('script');
            recaptchaSript.src = `https://www.google.com/recaptcha/api.js?render=6LfZnvkqAAAAAPIi3AR9sofIAb_x4P2vIHENvp-K`;
            recaptchaSript.async = true;
            recaptchaSript.defer = true;
            document.head.appendChild(recaptchaSript);
        }
    }

    loadClients = (params: { pageNumber: number; pageSize: number; search?: string }) => {
        return this.dbService.getClients(true, params.pageNumber, params.pageSize, params.search).pipe(
            map((res) => ({
                items: res.items,
                hasMore: res.items.length === params.pageSize,
                totalCount: res.totalCount,
            }))
        );
    };
}
